import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Lock, LogOut, UserCircle } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import Logo from '../ui/Logo';
import UserAvatar from '../ui/UserAvatar';
import SanitizeFinanceButton from '../ui/SanitizeFinanceButton';
import CrmThemeToggle from '../ui/CrmThemeToggle';
import NotificationSettingsModal from '../settings/NotificationSettingsModal';
import ProfileSettingsModal from '../settings/ProfileSettingsModal';
import ChangePasswordPanel from '../pm/ChangePasswordPanel';
import '../ui/AppHeader.css';
import '../pm/pm-shared.css';

const CpaHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const goFinances = useCallback(() => {
    navigate('/finances');
  }, [navigate]);

  return (
    <header className="app-header">
      <div className="header-left">
        <nav className="header-nav">
          <button
            type="button"
            className="header-brand"
            onClick={goFinances}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            aria-label="Go to Finances"
          >
            <Logo size="small" className="header-logo" />
          </button>
          <Link
            to="/finances"
            className={`nav-item ${location.pathname.startsWith('/finances') ? 'active' : ''}`}
          >
            Finances
          </Link>
        </nav>
      </div>

      <div className="header-right">
        <SanitizeFinanceButton />
        <CrmThemeToggle />
        <div className="user-menu-container" ref={menuRef}>
          <button
            type="button"
            className={`user-menu-trigger ${showMenu ? 'open' : ''}`}
            onClick={() => setShowMenu(!showMenu)}
            aria-expanded={showMenu}
            aria-haspopup="true"
          >
            <UserAvatar
              className="user-avatar"
              name={user?.name || 'User'}
              photoURL={user?.photoURL}
              userId={user?.id}
            />
            <span className="user-name">{user?.name}</span>
            <svg
              className={`dropdown-arrow ${showMenu ? 'open' : ''}`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-name">{user?.name}</span>
                <span className="dropdown-email">{user?.email}</span>
              </div>
              <div className="dropdown-divider" />
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowProfile(true); }}
              >
                <UserCircle size={16} />
                Profile
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); navigate('/settings/account'); }}
              >
                Account settings
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowNotifPrefs(true); }}
              >
                <Bell size={16} />
                Notification Settings
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { setShowMenu(false); setShowChangePassword(true); }}
              >
                <Lock size={16} />
                Change Password
              </button>
              <button
                type="button"
                className="dropdown-item logout"
                onClick={() => { setShowMenu(false); logout(); }}
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      {showNotifPrefs && (
        <NotificationSettingsModal onClose={() => setShowNotifPrefs(false)} variant="portal" />
      )}
      {showProfile && (
        <ProfileSettingsModal onClose={() => setShowProfile(false)} />
      )}
      {showChangePassword && (
        <ChangePasswordPanel onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
};

export default CpaHeader;
